/**
 * G04: ULDK Service
 * Parcel boundary lookup via GUGiK ULDK API
 */

import type { ParcelResult, ParcelError, GeocodingError, Result, WGS84Coordinate } from './types'
import { ok, err } from './types'
import { coordinateService } from './CoordinateService'
import { uugService } from './UUGService'
import { parseWktPolygon, calculatePolygonArea, parseULDKResponse } from './wktParser'
import { t } from '@/i18n'

const ULDK_BASE_URL = 'https://uldk.gugik.gov.pl/'
const MAX_RETRIES = 3
const RETRY_DELAYS = [1000, 2000, 4000]
const TIMEOUT_MS = 10000

export class ULDKService {
  private static instance: ULDKService

  static getInstance(): ULDKService {
    if (!ULDKService.instance) {
      ULDKService.instance = new ULDKService()
    }
    return ULDKService.instance
  }

  /**
   * Get parcel by WGS84 coordinates (Entry C: map click)
   */
  async getParcelByXY(coords: WGS84Coordinate): Promise<Result<ParcelResult, ParcelError>> {
    // ULDK expects coordinates as lon,lat with SRID
    const url = `${ULDK_BASE_URL}?request=GetParcelByXY&xy=${coords.lon},${coords.lat},4326&result=geom_wkt,id,voivodeship,county,commune,parcel`

    return this.fetchParcel(url)
  }

  /**
   * Get parcel by ID (Entry B: known parcel number)
   */
  async getParcelById(parcelId: string): Promise<Result<ParcelResult, ParcelError>> {
    const trimmed = parcelId.trim()
    if (!trimmed) {
      return err({
        type: 'not_found',
        message: 'Parcel ID cannot be empty'
      })
    }

    const url = `${ULDK_BASE_URL}?request=GetParcelById&id=${encodeURIComponent(trimmed)}&result=geom_wkt,id,voivodeship,county,commune,parcel`

    return this.fetchParcel(url)
  }

  /**
   * Get parcel by address (Entry A: combines UUG + ULDK)
   */
  async getParcelByAddress(address: string): Promise<Result<ParcelResult, GeocodingError | ParcelError>> {
    // First, geocode the address
    const geocodeResult = await uugService.geocode(address)

    if (!geocodeResult.ok) {
      return geocodeResult
    }

    // Then, get parcel at geocoded coordinates
    const parcelResult = await this.getParcelByXY(geocodeResult.value.wgs84)

    if (!parcelResult.ok) {
      return parcelResult
    }

    // Attach the address to the parcel result
    return ok({
      ...parcelResult.value,
      address: geocodeResult.value.address
    })
  }

  /**
   * G14: Get building footprint by PL-2000 coordinates
   * Returns building WKT geometry and ID, or null if no building at this point
   */
  async getBuildingByXY(x: number, y: number): Promise<{ id: string; wkt: string; functionCode?: string } | null> {
    const url = `${ULDK_BASE_URL}?request=GetBuildingByXY&xy=${x},${y}&result=geom_wkt,id,funkcja_ogolna&srid=2180`

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

      const response = await fetch(url, { signal: controller.signal })
      clearTimeout(timeoutId)

      if (!response.ok) return null

      const text = await response.text()
      const lines = text.trim().split('\n')
      if (lines.length < 2) return null

      const status = parseInt(lines[0], 10)
      if (status !== 0) return null

      const parts = lines[1].split('|')
      if (parts.length < 2) return null

      return {
        wkt: parts[0],
        id: parts[1],
        functionCode: parts[2] || undefined,
      }
    } catch {
      return null
    }
  }

  private async fetchParcel(url: string): Promise<Result<ParcelResult, ParcelError>> {
    let lastError: Error | null = null

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

        const response = await fetch(url, {
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const text = await response.text()
        return this.parseResponse(text)

      } catch (error) {
        lastError = error as Error

        if ((error as Error).name === 'AbortError') {
          lastError = new Error('Request timed out')
        }

        // Wait before retry (except on last attempt)
        if (attempt < MAX_RETRIES - 1) {
          await this.delay(RETRY_DELAYS[attempt])
        }
      }
    }

    return err({
      type: 'network',
      message: lastError?.message || 'Network error after retries'
    })
  }

  private parseResponse(text: string): Result<ParcelResult, ParcelError> {
    try {
      const parsed = parseULDKResponse(text)

      if (!parsed) {
        const lines = text.trim().split('\n')
        const firstLine = lines[0]

        if (firstLine.startsWith('-1')) {
          // Check if regional WMS service is down (second line mentions unresponsive service)
          const secondLine = lines[1] || ''
          if (secondLine.includes('nie zwróciła odpowiedzi') || secondLine.includes('usługa')) {
            return err({
              type: 'network',
              message: t('map.modal.error.serviceUnavailable')
            })
          }

          return err({
            type: 'not_found',
            message: 'No parcel found at this location'
          })
        }

        return err({
          type: 'parse',
          message: 'Failed to parse ULDK response'
        })
      }

      // Parse WKT geometry
      const boundaryPl2000 = parseWktPolygon(parsed.geomWkt)

      if (boundaryPl2000.length < 3) {
        return err({
          type: 'parse',
          message: 'Invalid polygon: less than 3 vertices'
        })
      }

      // Convert to WGS84
      const boundary = coordinateService.pl2000ArrayToWgs84(boundaryPl2000)

      // Calculate area
      const area = calculatePolygonArea(boundaryPl2000)

      const parcelResult: ParcelResult = {
        id: parsed.id,
        boundary,
        boundaryPl2000,
        region: {
          voivodeship: parsed.voivodeship,
          county: parsed.county,
          commune: parsed.commune,
          parcelNumber: parsed.parcel
        },
        area
      }

      return ok(parcelResult)

    } catch (error) {
      return err({
        type: 'parse',
        message: `Failed to parse parcel data: ${(error as Error).message}`
      })
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

// Export singleton instance
export const uldkService = ULDKService.getInstance()
